/**
 * User Access Hook for Shell App
 *
 * STRICT RBAC - Provides access control based on:
 * 1. is_superadmin = MASTER ROLE (bypasses all permission checks)
 * 2. Role flags = DATA SCOPE (who can see)
 *    - is_rc_hrbp: HRBP can see all users
 *    - is_rc_executive: Executive can see all users
 *    - is_rc_manager: Manager can see reportees
 *    - is_rc_employee: Employee can see self only
 * 3. permissions JSON = FEATURE ACCESS (what can do)
 *    - NO hardcoded bypasses - all access from permissions
 */

import { useAuth } from '../context/AuthContextBackend';
import {
  Action,
  PerformSparkResource,
  SimpleResource,
  type AccessCheck,
  Access,
  type AccessKey,
} from '../types/access';

interface UserAccess {
  // Loading state - CRITICAL for preventing race conditions
  isLoading: boolean;

  // Master role (bypasses all permission checks)
  isSuperAdmin: boolean;

  // Role flags (DATA SCOPE - who can see)
  isHRBP: boolean;        // Combined: is_scoped_hrbp OR is_global_hrbp
  isScopedHRBP: boolean;  // is_scoped_hrbp only (scoped HRBP / HR Head)
  isGlobalHRBP: boolean;  // is_global_hrbp only (org-wide HRBP)
  isExecutive: boolean;   // is_executive (standalone)
  isManager: boolean;     // is_manager (separate from executive)
  isEmployee: boolean;    // is_employee
  isAdmin: boolean;

  // Legacy aliases for backward compatibility
  /** @deprecated Use isHRBP instead */
  isHRManager: boolean;
  /** @deprecated Use isManager instead */
  isRCManager: boolean;
  /** @deprecated Use isEmployee instead */
  isRCEmployee: boolean;

  // Coaching role flags (from /me endpoint)
  isCoach: boolean;
  isCoachee: boolean;

  // Customer RBAC flags (from /me endpoint)
  isCustomerAdmin: boolean;
  customerAccess: string[];

  // Convenience helpers (DATA SCOPE)
  hasDirectReports: boolean;
  isAnyManager: boolean;
  canManageCycles: boolean;
  canViewAllData: boolean;

  // Access matrix checker functions (FEATURE ACCESS)
  hasModuleAccess: (resourcePath: string | PerformSparkResource | SimpleResource, action?: Action | string) => boolean;
  hasAccess: (accessKey: AccessKey) => boolean;
  hasAnyPermission: (accessKeys: AccessKey[]) => boolean;
  can: (check: AccessCheck) => boolean;

  // Feature flags (from account)
  canManageCustomers: boolean;

  // Customer module access check (LMS, TMS, Coaching)
  hasCustomerModuleAccess: (module: 'LMS' | 'TMS' | 'Coaching') => boolean;
}

/**
 * Use this hook to check user access in shell-app components
 *
 * STRICT RBAC - NO hardcoded bypasses
 * All access comes from permissions JSON in JWT
 */
/**
 * Helper function to normalize boolean values that may come as strings
 * Handles: true, false, "true", "false", undefined, null
 */
const toBool = (value: any): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
};

export function useUserAccess(): UserAccess {
  const { user, account, isLoading } = useAuth();

  // If loading or no user, return loading state (don't deny yet!)
  if (isLoading || !user) {
    return {
      // Loading state - CRITICAL: RouteGuard and Sidebar should wait when this is true
      isLoading: true,
      // Master role
      isSuperAdmin: false,
      // Role flags
      isHRBP: false,
      isScopedHRBP: false,
      isGlobalHRBP: false,
      isExecutive: false,
      isManager: false,
      isEmployee: false,
      isAdmin: false,
      // Legacy aliases
      isHRManager: false,
      isRCManager: false,
      isRCEmployee: false,
      // Coaching
      isCoach: false,
      isCoachee: false,
      // Customer RBAC
      isCustomerAdmin: false,
      customerAccess: [],
      // Helpers
      hasDirectReports: false,
      isAnyManager: false,
      canManageCycles: false,
      canViewAllData: false,
      canManageCustomers: false,
      hasModuleAccess: () => false,
      hasAccess: () => false,
      hasAnyPermission: () => false,
      can: () => false,
      hasCustomerModuleAccess: () => false,
    };
  }

  // SUPERADMIN - master role that bypasses all permission checks
  // Use toBool to handle both boolean and string values from API/JWT
  const isSuperAdmin = toBool(user.is_superadmin);

  // Extract role flags from user (DATA SCOPE - who can see)
  // These come from /me endpoint (RBAC flags no longer in Bearer token)
  const isScopedHRBP = toBool(user.is_rc_hrbp);      // Scoped HRBP (includes HR Heads)
  const isGlobalHRBP = toBool(user.is_hrmanager);   // Global HRBP
  const isHRBP = isScopedHRBP || isGlobalHRBP;      // Combined HRBP flag
  const isExecutive = toBool(user.is_rc_executive); // Executive (standalone flag)
  const isManager = toBool(user.is_rc_manager);     // Manager (separate from executive)
  const isEmployee = toBool(user.is_rc_employee);
  const isAdmin = toBool(user.is_admin);

  // Coaching role flags
  const isCoach = toBool(user.is_coach);
  const isCoachee = toBool(user.is_coachee);

  // Legacy aliases for backward compatibility
  const isHRManager = isHRBP;
  const isRCManager = isManager;
  const isRCEmployee = isEmployee;

  // Customer RBAC flags (from /me endpoint - is_customer_admin, access[])
  const isCustomerAdmin = toBool((user as any).is_customer_admin);
  const customerAccess: string[] = (user as any).access || [];

  // Convenience computed values (DATA SCOPE)
  // Note: isSuperAdmin is included in all convenience helpers for full access
  const hasDirectReports = isSuperAdmin || isManager || isExecutive;
  const isAnyManager = isSuperAdmin || isHRBP || isExecutive || isManager;
  const canManageCycles = isSuperAdmin || isHRBP; // Superadmin or HRBP can manage cycles
  const canViewAllData = isSuperAdmin || isHRBP || isExecutive || isAdmin;

  /**
   * Check if user has access to a module/resource via access matrix
   *
   * @param resourcePath - The resource path (e.g., "performspark.performance_management.cycles", "cycle-management")
   * @param action - The action to check (create, edit, delete, view). If not specified, checks if resource exists
   * @returns true if user has access, false otherwise
   *
   * @example
   * // Check if user has any access to cycles
   * hasModuleAccess('performspark.performance_management.cycles') // true if key exists
   *
   * // Check if user can create cycles
   * hasModuleAccess('performspark.performance_management.cycles', 'create') // true if 'create' in array
   *
   * // Also supports simplified paths
   * hasModuleAccess('cycle-management') // maps to 'performspark.performance_management.cycles'
   */
  const hasModuleAccess = (resourcePath: string | PerformSparkResource | SimpleResource, action?: Action | string): boolean => {
    // SUPERADMIN BYPASS - full access to everything
    if (isSuperAdmin) {
      return true;
    }

    if (!user.access_matrix) {
      return false;
    }

    // Map simplified paths to full resource paths
    const resourceMapping: Record<string, string> = {
      'cycle-management': 'performspark.performance_management.cycles',
      'goal-management': 'performspark.goal_management.goal_cycles',
      'performance-management': 'performspark.performance_management.cycles',
      'talent-management': 'performspark.talent_management.cycles',
      'survey-management': 'performspark.survey_management.templates',
      'training-management': 'learn.tms.events',
    };

    // Convert resourcePath to string (handles enums)
    const resourcePathStr = String(resourcePath);

    // Use mapped path if available, otherwise use the provided path
    const fullResourcePath = resourceMapping[resourcePathStr] || resourcePathStr;

    // Convert action to string if it's an enum
    const actionStr = action ? String(action) : undefined;

    // ADDITIVE APPROACH: Check ALL modules for the permission
    // If ANY module grants the permission, return true
    for (const [, resources] of Object.entries(user.access_matrix)) {
      // Check if this resource exists in the module
      if (resources[fullResourcePath]) {
        const permissions = resources[fullResourcePath];

        // If no specific action is requested, return true if resource exists
        if (!actionStr) {
          if (permissions.length > 0) {
            return true;
          }
        } else {
          // Check if the specific action is in the permissions array
          const permissionSet = new Set(permissions);
          if (permissionSet.has(actionStr)) {
            return true; // Found permission in this module - grant access immediately
          }
        }
      }

      // Also check if the resourcePath matches a partial key
      // e.g., "cycle" might match "performspark.performance_management.cycles"
      for (const [resourceKey, permissions] of Object.entries(resources)) {
        if (resourceKey.endsWith(`.${resourcePathStr}`) || resourceKey === resourcePathStr) {
          if (!actionStr) {
            if (permissions.length > 0) {
              return true;
            }
          } else {
            const permissionSet = new Set(permissions);
            if (permissionSet.has(actionStr)) {
              return true; // Found permission in this module - grant access immediately
            }
          }
        }
      }
    }

    // Only return false if no module granted the permission
    return false;
  };

  /**
   * Type-safe access check using predefined Access constants
   *
   * @param accessKey - Key from Access constant object
   * @returns true if user has access, false otherwise
   *
   * @example
   * // Type-safe with autocomplete!
   * if (hasAccess('CYCLE_CREATE')) {
   *   // Show create button
   * }
   */
  const hasAccess = (accessKey: AccessKey): boolean => {
    // SUPERADMIN BYPASS - full access to everything
    if (isSuperAdmin) {
      return true;
    }
    const { resource, action } = Access[accessKey];
    return hasModuleAccess(resource, action);
  };

  /**
   * Flexible access check using AccessCheck object
   *
   * @param check - Object with resource and optional action
   * @returns true if user has access, false otherwise
   *
   * @example
   * // Using enums
   * if (can({ resource: ReviewCloudResource.CYCLE, action: Action.CREATE })) {
   *   // Show create button
   * }
   *
   * @example
   * // Without action (checks if resource exists)
   * if (can({ resource: SimpleResource.CYCLE_MANAGEMENT })) {
   *   // Show cycles menu
   * }
   */
  const can = (check: AccessCheck): boolean => {
    // SUPERADMIN BYPASS - full access to everything
    if (isSuperAdmin) {
      return true;
    }
    return hasModuleAccess(check.resource, check.action);
  };

  /**
   * Check if user has ANY of the specified permissions
   *
   * @param accessKeys - Array of AccessKey to check
   * @returns true if user has at least one of the permissions
   */
  const hasAnyPermission = (accessKeys: AccessKey[]): boolean => {
    // SUPERADMIN BYPASS - full access to everything
    if (isSuperAdmin) {
      return true;
    }
    return accessKeys.some((key) => hasAccess(key));
  };

  /**
   * Check if user has access to a specific customer module (LMS, TMS, Coaching)
   * Customer module access comes from the account.access array via /me endpoint
   */
  const hasCustomerModuleAccess = (module: 'LMS' | 'TMS' | 'Coaching'): boolean => {
    return customerAccess.includes(module);
  };

  return {
    // Loading state
    isLoading: false,
    // Master role
    isSuperAdmin,
    // Role flags (DATA SCOPE)
    isHRBP,
    isScopedHRBP,
    isGlobalHRBP,
    isExecutive,
    isManager,
    isEmployee,
    isAdmin,
    // Legacy aliases
    isHRManager,
    isRCManager,
    isRCEmployee,
    // Coaching
    isCoach,
    isCoachee,
    // Customer RBAC
    isCustomerAdmin,
    customerAccess,
    // Helpers
    hasDirectReports,
    isAnyManager,
    canManageCycles,
    canViewAllData,
    // Feature flags (from account)
    canManageCustomers: account?.can_manage_customers ?? false,
    // Permission checkers (FEATURE ACCESS)
    hasModuleAccess,
    hasAccess,
    hasAnyPermission,
    can,
    hasCustomerModuleAccess,
  };
}
